import React from 'react'
import { useApp } from '../../context/AppContext'
import { Service, api } from '../../api/client'
import type { Package } from '../../types/app'
import { priceOrFree } from '../../catalog/format'
import Panel, { PanelStatus } from './Panel'
import PackageSheet from './PackageSheet'
import { saveErrorMessage, showToast } from './toast'

export default function PackagesPanel() {
  const { dispatch } = useApp()
  const [status, setStatus] = React.useState<'loading' | 'error' | 'ready'>('loading')
  const [packages, setPackages] = React.useState<Package[]>([])
  const [services, setServices] = React.useState<Service[]>([])
  const [editing, setEditing] = React.useState<Package | 'new' | null>(null)

  const load = React.useCallback(async () => {
    setStatus('loading')
    try {
      const [loadedPackages, loadedServices] = await Promise.all([api.listPackages(), api.listServices()])
      setPackages(loadedPackages)
      setServices(loadedServices)
      setStatus('ready')
    } catch {
      setStatus('error')
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  // refresh fetches the list again while keeping the current one on screen.
  const refresh = React.useCallback(async () => {
    try {
      const [loadedPackages, loadedServices] = await Promise.all([api.listPackages(), api.listServices()])
      setPackages(loadedPackages)
      setServices(loadedServices)
    } catch {
      // Keep showing the list we have; the next open loads it again.
    }
  }, [])

  // open fetches services again as the sheet opens: she may have just added
  // one in the Services panel above, and this panel's list predates it.
  const open = (target: Package | 'new') => {
    setEditing(target)
    refresh()
  }

  const saved = (message: string) => {
    setEditing(null)
    refresh()
    showToast(dispatch, message)
  }

  // Names the services the package covers; a service with several lengths
  // shows the ones covered, like "Facial (60, 90 min)".
  const serviceNames = (pkg: Package) => {
    const byService = new Map<string, number[]>()
    for (const covered of pkg.lengths) {
      byService.set(covered.service_id, [...(byService.get(covered.service_id) ?? []), covered.duration_minutes])
    }
    return [...byService.entries()]
      .flatMap(([serviceId, minutes]) => {
        const service = services.find((s) => s.id === serviceId)
        if (!service) {
          return []
        }
        return [service.lengths.length > 1 ? `${service.name} (${minutes.join(', ')} min)` : service.name]
      })
      .join(', ')
  }

  return (
    <>
      <Panel title="Packages" intro="Prepaid blocks of sessions. Sell one from a client’s profile. Once card payments are on, clients can also buy them on your booking page.">
        {status !== 'ready' ? (
          <PanelStatus status={status} onRetry={load} />
        ) : (
          <>
            {packages.length === 0 ? (
              <p className="settings-status">No packages yet. Add your first one below.</p>
            ) : (
              <ul className="service-list">
                {packages.map((pkg) => (
                  <li key={pkg.id} className="service-item">
                    <button type="button" className="service-item__main" onClick={() => open(pkg)}>
                      <span className="service-item__name">{pkg.name}</span>
                      <span className="service-item__meta">
                        {pkg.session_count} sessions · {priceOrFree(pkg.price_pence)}
                        {serviceNames(pkg) ? ` · ${serviceNames(pkg)}` : ''}
                        {pkg.sellable_online === false ? ' · not sold online' : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button type="button" className="btn btn-primary btn-block" onClick={() => open('new')}>
              Add package
            </button>
          </>
        )}
      </Panel>
      {editing && (
        <PackageSheet
          pkg={editing === 'new' ? null : editing}
          services={services}
          onClose={() => setEditing(null)}
          onSaved={saved}
        />
      )}
    </>
  )
}
